import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { isValid, parse } from 'date-fns';
import './lease-form.scss';
import { SectionTitle, SectionContent, HighlightedText } from '../../../../../../../leaseClauses/LeaseTextPrimitives';
import { UtilitiesClause, UTILITIES_MAP, formatList } from '../../../../../../../leaseClauses/UtilitiesClause';

// Constants

const LATE_FEE = 150.00;
const NSF_FEE = 150.00;
const PET_NON_REFUNDABLE_FEE = 350.00;

// Utility functions

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const cleanDateStr = dateString.includes('T') ? dateString.split('T')[0] : dateString;
  const date = parse(cleanDateStr, 'yyyy-MM-dd', new Date());
  if (!isValid(date)) return '-';

  try {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    const getSuffix = (day) => {
      if ([1, 21, 31].includes(day)) return 'st';
      if ([2, 22].includes(day)) return 'nd';
      if ([3, 23].includes(day)) return 'rd';
      return 'th';
    };

    return `${month} ${day}${getSuffix(day)}, ${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

const formatAmount = (value) => {
  if (!value) return '';
  const numValue = parseFloat(value.toString().replace(/[$,]/g, '')) || 0;
  return `$${numValue.toFixed(2)}`;
};

// Pet content generator
const generatePetContent = (propertyDetails) => {
  const { allowed_pets } = propertyDetails || {};
  const { dogs = false, cats = false } = allowed_pets || {};

  if (cats && !dogs) {
    return (
      <>
        Only Cats under 20 lbs., full grown, are allowed with a {formatAmount(PET_NON_REFUNDABLE_FEE)}, pet nonrefundable fee.
        All animals must be spayed or neutered.
        No Dogs allowed during the tenancy period.
        Violation of this can lead to eviction.
        You must pick up after your dog.
        Management will also conduct an interview with pet if needed.
        All Pets must be approved by management prior to signing the lease or during tenancy.
      </>
    );
  }

  if (cats && dogs) {
    return (
      <>
        Cats and small dogs under 20 lbs., full grown, are allowed with a {formatAmount(PET_NON_REFUNDABLE_FEE)}, pet nonrefundable fee.
        All animals must be spayed or neutered.
        Dogs must always be managed outside of your apartment while on Property.
        You must pick up after your dog.
        Management will also conduct an interview with pet if needed.
        All Pets must be approved by management prior to signing the lease or during tenancy.
      </>
    );
  }

  if (dogs && !cats) {
    return (
      <>
        Only small dogs under 20 lbs., full grown, are allowed with a {formatAmount(PET_NON_REFUNDABLE_FEE)}, pet nonrefundable fee.
        All animals must be spayed or neutered.
        No Cats allowed during the tenancy period.
        Violation of this can lead to eviction.
        Dogs must always be managed outside of your apartment while on Property.
        You must pick up after your dog.
        Management will also conduct an interview with pet if needed.
        All Pets must be approved by management prior to signing the lease or during tenancy.
      </>
    );
  }

  return "No pets are allowed during the tenancy period. Violation of this can lead to eviction.";
};

// Section components

// Security Deposits component
const SecurityDeposits = ({ lease }) => {
  const securityDeposits = [
    {
      description: "Security Deposit (Refundable)",
      amount: formatAmount(lease?.security_deposits?.refundable) || ""
    },
    {
      description: "Pet Security Fee (Non-Refundable)",
      amount: formatAmount(lease?.security_deposits?.non_refundable) || ""
    }
  ];

  return (
    <Paper variant="outlined" className="security-details-table">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell>Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {securityDeposits
            .filter((deposit) => deposit.description === "Security Deposit (Refundable)")
            .map((deposit, index) => (
              <TableRow key={index}>
                <TableCell className="label">{deposit.description}</TableCell>
                <TableCell>{deposit.amount || '-'}</TableCell>
              </TableRow>
            ))}
          {securityDeposits
            .filter((deposit) => deposit.description === "Pet Security Fee (Non-Refundable)")
            .map((deposit, index) => (
              <TableRow key={index}>
                <TableCell className="label">{deposit.description}</TableCell>
                <TableCell>{deposit.amount || '-'}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

// Main Step2 component
const Step2 = ({ lease }) => {
  const notIncludedUtilities = Object.entries(lease?.property_details?.utilities_available || {})
    .filter(([_, isSelected]) => !isSelected)
    .map(([key]) => UTILITIES_MAP[key]);

  const utilitiesString = formatList(notIncludedUtilities);
  return (
    <Box className="lease-form-step2" sx={{ p: 2 }}>
      {/* Section 4: Security Deposit */}
      <Box className="section" sx={{ mb: 3 }}>
        <SectionTitle>4. SECURITY DEPOSIT/PET FEE DEPOSIT</SectionTitle>
        <SectionContent>
          The Landlord acknowledges receipt on <span
            style={{
              fontWeight: 600,
              color: '#1976d2',
            }}
          >
            {formatDate(lease?.security_deposits?.received_date)}
          </span> from the Tenant as a
          security deposit. The Security Deposit may be utilized by the Landlord in such amount
          as is necessary to provide for any or all of the following:
          <br />
          <SecurityDeposits lease={lease} />

          <Typography variant="subtitle2" className="section-bottom-content">
            A. The landlord is entitled to use and retain the security deposit in the event the tenant:
            does not take possession on the commencement of the term of this lease, does not pay the
            first installment of rent, or does not execute a lease agreement satisfactory to both parties.
          </Typography>

          <Typography variant="subtitle2" className="section-bottom-content">
            B. The landlord may apply such amounts as he/she deems necessary to provide for:
            <Box component="ol" sx={{ pl: 4 }}>
              <li>Repairing or replacing any fixtures, fittings, appliances, or facilities located in, upon or about the leased premises.</li>
              <li>Cleaning and arranging for cleaning of the leased premises; including appliances, windows, floors, walls, professionally steam cleaning the carpets, repairing paint work etc.</li>
              <li>To remedy a breach of an obligation of the tenant hereunder at any time.</li>
              <li style={{ backgroundColor: '#ffff99', color: 'black', borderRadius: '1px' }}>
                To cover the cost of any late rent or NSF/Returned cheque fee.
              </li>
              <li>The security deposit can NOT be used by the tenant towards any rent.</li>
            </Box>
          </Typography>
        </SectionContent>
      </Box>

      {/* Section 5: Pets */}
      <Box className="section">
        <SectionTitle>5. PETS</SectionTitle>
        <SectionContent>
          {generatePetContent(lease?.property_details)}
        </SectionContent>
      </Box>

      {/* Section 6: Rent Payment */}
      <Box className="section">
        <SectionTitle>6. RENT PAYMENT</SectionTitle>
        <SectionContent highlighted>
          <div className='pb-2'>
            <span className='text-black fw-bold'>Due Date:</span> Rent is due on the <HighlightedText>1st day</HighlightedText> of each month. <br />
          </div>
          <div className='pb-2'>
            <span className='text-black fw-bold'>Late Payment Fee:</span> A fee of <HighlightedText>${LATE_FEE.toFixed(2)}</HighlightedText> will be charged if the full rent is received on or after the <HighlightedText>2nd day</HighlightedText> of the month. <br />
          </div>
          <div className='pb-2'>
            <span className='text-black fw-bold'>Non-Sufficient Funds (NSF):</span> Should any payment be returned due to non-sufficient funds, the tenant agrees to pay: <br />
            <div className='ms-4'>
              <>🔸<HighlightedText>${NSF_FEE.toFixed(2)}</HighlightedText> NSF fee for each occurrence within <HighlightedText>48 hours</HighlightedText>.</> <br />
              <>🔸<HighlightedText>${LATE_FEE.toFixed(2)}</HighlightedText> late payment fee in addition within <HighlightedText>48 hours</HighlightedText>.</> <br />
            </div>
            <>Any funds received will first be applied to the outstanding balance and all Incentives can be forfeited.</> <br />
          </div>

          <div className='pb-2'>
            <span className='text-black fw-bold'>Eviction Notice:</span> If rent is not paid by the <HighlightedText>1st day</HighlightedText> of the month: <br />
            <div className='ms-4'>
              <>🔸The tenant will receive an <HighlightedText>eviction notice</HighlightedText> to vacate the property.</> <br />
              <>🔸The matter will be filed with the <HighlightedText>RTRDS</HighlightedText> unless payment arrangements have been made with management within <HighlightedText>24 hours</HighlightedText> and approved.</> <br />
            </div>
          </div>
          <div className='pb-2'>
            <><span className='text-black fw-bold'>Grounds for Eviction:</span> Any NSF occurrence within the first <HighlightedText>4 months</HighlightedText> of the lease term may constitute grounds for eviction and all Incentives can be forfeited.</><br />
          </div>
        </SectionContent>
      </Box>

      {/* Section 7: Utilities */}
      <UtilitiesClause
        utilitiesString={utilitiesString}
        notIncludedUtilities={notIncludedUtilities}
        customUtilitiesClause={lease?.property_details?.custom_utilities_clause}
      />
    </Box>
  );
};

export { Step2 };